"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Building2, Calendar, FileCheck2, Clock, HardHat, BadgeCheck, PhoneCall, ArrowUpRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyPillars = [{
  title: "Dedicated contract supervisor",
  description: "One single point of contact on site across every project phase, reducing handover delays and communication gaps.",
  icon: HardHat
}, {
  title: "Strict safety compliance",
  description: "Fully accredited under NASC and CHAS with comprehensive handover certificates on every erection.",
  icon: ShieldCheck
}, {
  title: "Agreed programme reliability",
  description: "Scheduled erection, phased adaptation, and strike dates matched precisely to your principal contractor timeline.",
  icon: Calendar
}, {
  title: "Transparent commercial terms",
  description: "Clear fixed scopes, honest variations, and dependable hire periods without hidden costs or sudden surcharges.",
  icon: FileCheck2
}];
const relationshipHighlights = [{
  metric: "20+",
  label: "Years of frontline experience",
  detail: "Led by Pete Wood and our senior management team across the South East."
}, {
  metric: "100%",
  label: "Accredited workforce",
  detail: "CISRS certified scaffolders adhering to strict TG20 and SG4 safety standards."
}, {
  metric: "6+",
  label: "Core counties served",
  detail: "Rapid response teams covering Berkshire, Surrey, Hampshire, Bucks, London, and Home Counties."
}];
export default function Partnership() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  return <section id="longtermrelationshipcase" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Header Area */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariants} className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="border-border text-foreground font-medium px-3 py-1">
              Case study &amp; client partnerships
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight leading-tight">
            Long-term contractor partnerships built on reliability
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Repeat commercial contractors, roofing specialists, and developers trust BMF Scaffolding for structured multi-phase access, disciplined safety governance, and straightforward communication on site.
          </p>
        </motion.div>

        {/* Featured Case Overview Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariants} className="mb-16">
          <Card className="border border-border shadow-md overflow-hidden bg-card">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Left visual & context */}
              <div className="lg:col-span-5 relative bg-secondary text-secondary-foreground p-8 sm:p-10 flex flex-col justify-between overflow-hidden">
                <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-primary/10 pointer-events-none blur-3xl" />
                <div>
                  <div className="flex items-center gap-2 text-primary text-sm font-semibold mb-3">
                    <Building2 className="size-4" />
                    <span>Framework &amp; multi-site access</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-secondary-foreground mb-4">
                    The partnership approach in practice
                  </h3>
                  <p className="text-secondary-foreground/80 text-sm leading-relaxed mb-6">
                    Rather than treating each project as a one-off transaction, we work alongside main contractors, regional builders, and housing developers to plan phased access across continuous development cycles.
                  </p>
                </div>

                <div className="space-y-4 pt-6 border-t border-border/30">
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="size-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-secondary-foreground/90">
                      Standardised risk assessments, method statements, and TG20 compliance packs ready before arrival.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="size-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-secondary-foreground/90">
                      Responsive support and interim inspection sign-offs maintained with seven-day statutory checks.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right content details */}
              <div className="lg:col-span-7 p-6 sm:p-10 flex flex-col justify-between bg-card">
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Focus</span>
                      <h4 className="text-lg font-semibold text-card-foreground">Commercial &amp; Multi-Phase Access</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-muted text-foreground border border-border">
                        <Clock className="size-3.5 text-primary" />
                        Multi-Year Continuity
                      </span>
                    </div>
                  </div>

                  <Separator className="mb-6" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    <div className="space-y-2">
                      <h5 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        Operational continuity
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Consistent scaffolding crews familiar with site-specific protocols, logistics constraints, and working-at-height requirements.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        Accreditation safety shield
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Certified NASC contractor standards and CHAS accreditation safeguarding site managers.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        Programmed adaptations
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Rapid adjustments for follow-on trades including bricklayers, carpenters, window fitters, and roofing teams.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        Family accountability
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Direct director involvement from Pete Wood and senior managers with genuine personal commitment to every contract.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Internal Page Deep Links */}
                <div className="pt-6 border-t border-border flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <Link to="/HealthSafetyAccreditationsPage" className="text-xs sm:text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                      Safety &amp; Accreditations
                      <ArrowUpRight className="size-3.5" />
                    </Link>
                    <span className="text-muted-foreground text-xs">•</span>
                    <Link to="/CommercialScaffoldingPage" className="text-xs sm:text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                      Commercial Scaffolding
                      <ArrowUpRight className="size-3.5" />
                    </Link>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    South East &amp; Home Counties coverage
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 4 Pillars Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={staggerContainer} className="mb-16">
          <div className="mb-8">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
              What keeps clients working with BMF
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              The four operational commitments we bring to every scaffolding framework and agreement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyPillars.map((pillar, i) => {
            const IconComp = pillar.icon;
            return <motion.div key={pillar.title} variants={fadeInVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card p-6 rounded-xl hover:border-primary/50 transition-[border-color,box-shadow] shadow-xs">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit mb-4">
                      <IconComp className="size-5" />
                    </div>
                    <CardTitle className="text-base font-semibold text-card-foreground mb-2">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </Card>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Metrics Strip */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariants} className="mb-16 bg-muted/50 border border-border rounded-xl p-8 sm:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-border">
            {relationshipHighlights.map((item, i) => <div key={item.label} data-index={i} className={`flex flex-col justify-center ${i > 0 ? "pt-6 md:pt-0 md:pl-8" : ""}`}>
                <span className="text-3xl sm:text-4xl font-extrabold text-primary tracking-tight font-default">
                  {item.metric}
                </span>
                <span className="text-sm font-semibold text-foreground mt-1">
                  {item.label}
                </span>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  {item.detail}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Bottom CTA Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariants} className="bg-card border border-border rounded-2xl p-8 sm:p-12 text-center max-w-4xl mx-auto shadow-md">
          <Badge variant="secondary" className="mb-4">
            Start a working relationship
          </Badge>
          <h3 className="text-2xl sm:text-3xl font-bold font-default text-card-foreground tracking-tight mb-3">
            Ready to discuss your next development or scheduled tender?
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Whether you need comprehensive access for a long-term commercial build, recurring roofing contracts, or immediate site pricing, our estimators and contracts team are on hand.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4" />
                Discuss a Project
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                Request a Quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-primary" />
              NASC Certified
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-primary" />
              CHAS Accredited
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}